import { Component, DestroyRef, inject, HostListener } from '@angular/core';
import { ColumnDefinition, ColumnRendererComponent, IconRendererComponent, UserState } from '@gea/digital-ui-lib';
import { MachineService } from '../services/machine.service';
import { Machine } from '../models/machine';
import { NotificationService } from '../services/notification.service';
import { Notification } from '../models/notification';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { NotificationComponent } from '../notification/notification.component';
import { environment } from '../../environments/environment';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
  standalone: false,

})
export class DashboardComponent {
  private machineService = inject(MachineService);
  private notificationService = inject(NotificationService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private dialog = inject(MatDialog);

  loading = false;
  errorString = '';
  activeSection = 'notifications';
  notifications: Notification[] = [];
  machines: Machine[] = [];
  displayedColumns: string[] = ['name', 'status'];
  isMobileView: boolean = false;
  private readonly statusTimeoutMinutes = environment.statusTimeoutMinutes;

  columnDefinitions: ColumnDefinition[] = [
    {
      displayName: 'Status',
      key: 'displayStatus', // Use displayStatus instead of status
      renderer: {
        component: IconRendererComponent as ColumnRendererComponent<unknown>,
        config: {
          mapping: {
            '-1': '16px_offline', // Grey for offline/stale
            0: '16px_check',      // Green for OK
            1: '16px_warning-sign', // Red for warning
          },
        },
      },
    },
    {
      displayName: 'Name',
      key: 'name',
    },
  ];

  ngOnInit() {
    this.checkMobileView();
    this.route.paramMap.subscribe(params => {
      const activeSectionParm = params.get('activeSection');
      this.activeSection = activeSectionParm || 'notifications';
    });

    this.loading = true;

    forkJoin({
      notifications: this.notificationService.getNotifications(),
      machines: this.machineService.getMachines(),
    }).subscribe({
      next: ({ notifications, machines }) => {
        // set notifications and sort by date
        this.notifications = notifications;
        this.notifications = this.notifications.sort((a, b) => {
          return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
        });

        // set machines, calculate display status, and sort by name
        this.machines = machines.map(m => ({
          ...m,
          displayStatus: this.calculateDisplayStatus(m)
        }));
        this.machines = this.machines.sort((a, b) => {
          return a.name.localeCompare(b.name);
        });

        this.loading = false;
      },
      error: (error) => {
        this.errorString = error.message;
        this.loading = false;
      }
    });
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    this.checkMobileView();
  }

  private checkMobileView() {
    this.isMobileView = window.innerWidth <= 768;
  }

  public openNotification(notification: Notification) {
    if (this.isMobileView) {
      // Navigate to notification page on mobile
      this.router.navigate(['/notification', notification.id]);
    } else {
      // Open modal on desktop
      this.dialog.open(NotificationComponent, {
        data: notification,
        width: '720px',
        height: '60vh',
      });
    }
  }

  public openMachine(machine: Machine) {
    // For now, machines navigate to notifications
    // Can be extended with a dedicated machine modal later
    this.router.navigate(['/notifications', machine.id]);
  }

  showSection(section: string) {
    this.activeSection = section;
  }

  /**
   * Calculates the display status for a machine, taking into account data staleness.
   * Returns -1 (grey) if data is older than configured threshold (default 24 hours), otherwise returns the actual status.
   */
  private calculateDisplayStatus(machine: Machine): number {
    if (!machine.oldestStatusCheck) {
      // No timestamp available, show grey (offline)
      return -1;
    }

    const now = new Date().getTime();
    const oldestCheck = new Date(machine.oldestStatusCheck).getTime();
    const minutesSinceCheck = (now - oldestCheck) / 1000 / 60;

    if (minutesSinceCheck > this.statusTimeoutMinutes) {
      // Data is stale, show grey (offline)
      return -1;
    }

    // Data is fresh, use actual status
    return machine.status;
  }
}
